import airflowCodetf from './airflow.codetf.json'
import airflow from './airflow.json'
import juiceShopCodetf from './juice-shop.codetf.json'
import juiceShop from './juice-shop.json'
import rollerCodetf from './roller.codetf.json'
import roller from './roller.json'
import type { AntaresFinding, CodeTF, CodeTFFixResult, RunReport } from './types.ts'
import webgoatCodetf from './webgoat.codetf.json'
import webgoat from './webgoat.json'

export type {
  AntaresFinding,
  CodeTF,
  CodeTFChangeSet,
  CodeTFFixMetadata,
  CodeTFFixQuality,
  CodeTFFixResult,
  CodeTFFixStatus,
  CodeTFRating,
  CwePlanEntry,
  DetectionOutcome,
  RunReport,
  TriageLocation,
  TriageVerdict,
} from './types.ts'

export type AntaresRunCatalogEntry = {
  slug: string
  label: string
  kind: string
  language: string
  report: RunReport
}

export const antaresRunCatalog: AntaresRunCatalogEntry[] = [
  {
    slug: 'webgoat',
    label: 'Webgoat-Demo',
    kind: 'Deliberately vulnerable',
    language: 'Java',
    report: webgoat as unknown as RunReport,
  },
  {
    slug: 'juice-shop',
    label: 'Juice Shop',
    kind: 'Deliberately vulnerable',
    language: 'TypeScript',
    report: juiceShop as unknown as RunReport,
  },
  {
    slug: 'roller',
    label: 'Apache Roller',
    kind: 'Mature production app',
    language: 'Java',
    report: roller as unknown as RunReport,
  },
  {
    slug: 'airflow',
    label: 'Apache Airflow',
    kind: 'Large production monorepo',
    language: 'Python',
    report: airflow as unknown as RunReport,
  },
]

export function getAntaresRun(slug: string): AntaresRunCatalogEntry | undefined {
  return antaresRunCatalog.find(entry => entry.slug === slug)
}

// CodeTF fix results are keyed by slug. The JSON is imported once and reused via lookups so we do
// not repeatedly re-cast the same value. Static JSON is inferred by TypeScript as a wide
// `string`-shaped literal; the fixStatus.status union and other narrow shapes cannot be
// reconstructed structurally, so we cast at the seam. Matches the existing
// `webgoat as unknown as RunReport` pattern elsewhere in this file.
const codetfBySlug: Record<string, CodeTF> = {
  airflow: airflowCodetf as unknown as CodeTF,
  'juice-shop': juiceShopCodetf as unknown as CodeTF,
  roller: rollerCodetf as unknown as CodeTF,
  webgoat: webgoatCodetf as unknown as CodeTF,
}

export const getCodetfForSlug = (slug: string): CodeTF | undefined => codetfBySlug[slug]

// Antares repo-level analyses (threat-model / finding-overlay / scorecards) are generated per slug
// and dropped into this directory as JSON. Not every slug has every artifact yet, so we discover
// what is on disk with a Vite glob rather than importing each file explicitly — a missing file would
// otherwise fail the build. Each map key is the slug portion of the filename, extracted from the
// path so we do not have to hard-code which slugs are present.
const extractSlugFromPath = (path: string, suffix: string): string => {
  const filename = path.split('/').pop() ?? ''
  return filename.replace(suffix, '')
}

const buildSlugMap = (modules: Record<string, { default: unknown }>, suffix: string): Record<string, unknown> => {
  const map: Record<string, unknown> = {}
  for (const [path, module] of Object.entries(modules)) {
    const slug = extractSlugFromPath(path, suffix)
    // Vite's eager JSON glob wraps each module in `{ default: <payload> }`; unwrap to the raw JSON.
    map[slug] = module.default
  }
  return map
}

const threatModelBySlug = buildSlugMap(
  import.meta.glob<{ default: unknown }>('./*.threat-model.json', { eager: true }),
  '.threat-model.json'
)
const threatModelContentBySlug = buildSlugMap(
  import.meta.glob<{ default: unknown }>('./*.threat-model.content.json', { eager: true }),
  '.threat-model.content.json'
)
const scorecardBySlug = buildSlugMap(
  import.meta.glob<{ default: unknown }>('./*.scorecard.json', { eager: true }),
  '.scorecard.json'
)
const findingOverlayBySlug = buildSlugMap(
  import.meta.glob<{ default: unknown }>('./*.finding-overlay.json', { eager: true }),
  '.finding-overlay.json'
)
const findingOverlayContentBySlug = buildSlugMap(
  import.meta.glob<{ default: unknown }>('./*.finding-overlay.content.json', { eager: true }),
  '.finding-overlay.content.json'
)

export const getAntaresThreatModelForSlug = (slug: string): unknown => threatModelBySlug[slug]
export const getAntaresThreatModelContentForSlug = (slug: string): unknown => threatModelContentBySlug[slug]
export const getAntaresScorecardForSlug = (slug: string): unknown => scorecardBySlug[slug]
export const getAntaresFindingOverlayForSlug = (slug: string): unknown => findingOverlayBySlug[slug]
export const getAntaresFindingOverlayContentForSlug = (slug: string): unknown => findingOverlayContentBySlug[slug]

export const getCodetfFixResultForFindingId = (slug: string, findingId: string): CodeTFFixResult | undefined => {
  const codetf = codetfBySlug[slug]
  if (!codetf) {
    return undefined
  }
  return codetf.results.find(result => result.finding.id === findingId)
}

export const getAntaresScanIdForSlug = (slug: string): string | undefined => getAntaresRun(slug)?.report.run_id

export const getAntaresRepositoryIdForSlug = (slug: string): string | undefined =>
  getAntaresRun(slug) ? `antares-repo-${slug}` : undefined

export type AntaresRunStats = {
  total: number
  findings: AntaresFinding[]
  investigations: AntaresFinding[]
  uncertain: AntaresFinding[]
  cwes: number
  incomplete: number
}

export function computeRunStats(report: RunReport): AntaresRunStats {
  const findings: AntaresFinding[] = []
  const investigations: AntaresFinding[] = []
  const uncertain: AntaresFinding[] = []
  for (const finding of report.findings) {
    const verdict = finding.triage?.result_type
    if (verdict === 'TRUE_POSITIVE') findings.push(finding)
    else if (verdict === 'UNCERTAIN') uncertain.push(finding)
    else if (verdict === 'FALSE_POSITIVE') investigations.push(finding)
  }
  return {
    total: report.findings.length,
    findings,
    investigations,
    uncertain,
    cwes: report.phases.detection.length,
    incomplete: report.phases.detection.filter(d => d.incomplete_reason).length,
  }
}
